import { useMemo } from "react";
import type { ValidCombinationDto } from "@normax-performance/shared-types";
import { Decimal, resolvePolicy } from "@normax-performance/calculation-engine";
import type { WorkLineInput } from "@normax-performance/calculation-engine";
import { formatStandardDisplay } from "@normax-performance/shared-format";
import { isNormedRow, type RowState } from "../daily-entry.types";
import { resolveMatchedNorm } from "../resolve-norm";

const policy = resolvePolicy("v1");

export interface DayAggregatePreview {
  hasNormedWork: boolean;
  performanceRatioRaw: string | null;
  performanceRatioLabel: string | null;
  deviationLabel: string | null;
  normedMinutesLabel: string;
}

/**
 * Local Method C preview across the day's current NORMED_WORK rows
 * (CALCULATION_ENGINE_ARCHITECTURE.md §5) — same "no normed work in period"
 * guard as the backend: never shows a fabricated 0%.
 */
export function useDayAggregatePreview(
  rows: RowState[],
  validCombinations: ValidCombinationDto[],
): DayAggregatePreview {
  return useMemo(() => {
    const lines: WorkLineInput[] = rows
      .filter(isNormedRow)
      .filter((r) => r.durationMinutes.trim() !== "" && r.reportedQuantity.trim() !== "")
      .map((row) => {
        const norm = resolveMatchedNorm(row, validCombinations);
        return {
          standardQuantity: norm ? new Decimal(norm.standardQuantity) : null,
          baseDurationMinutes: norm ? new Decimal(norm.baseDurationMinutes) : null,
          durationMinutes: new Decimal(row.durationMinutes),
          reportedQuantity: new Decimal(row.reportedQuantity),
        };
      });

    const result = policy.aggregate({ lines });
    const display = formatStandardDisplay({
      performanceRatioPercent: policy.rounding.formatPercentage(result.performanceRatioRaw),
      deviationPercent: policy.rounding.formatPercentage(result.deviationRaw),
      normedMinutes: policy.rounding.formatMinutes(result.totalNormedMinutes),
    });

    return {
      hasNormedWork: result.performanceRatioRaw !== null,
      performanceRatioRaw: result.performanceRatioRaw?.toString() ?? null,
      performanceRatioLabel: display.performanceRatioLabel,
      deviationLabel: display.deviationLabel,
      normedMinutesLabel: display.normedMinutesLabel,
    };
  }, [rows, validCombinations]);
}
